import React, { useEffect, useRef, useState, useMemo } from 'react';
import { notification } from 'antd';
import { Variable } from '@/types/TVariables';

interface TextEditorProps {
	value?: string;
	onChange?: (value: string, variables?: Record<string, Variable>) => void;
	variables?: Record<string, Variable>;
	placeholder?: string;
}

const TextEditor: React.FC<TextEditorProps> = ({
	value = '',
	onChange,
	variables = {},
	placeholder,
}) => {
	const inputRef = useRef<HTMLDivElement>(null);
	const [internalValue, setInternalValue] = useState<string>(value);
	const [showVariableList, setShowVariableList] = useState<boolean>(false);
	const [listPosition, setListPosition] = useState<{ top: number; left: number }>({
		top: 0,
		left: 0,
	});

	const currentRawValue = value !== undefined ? value : internalValue;

	const highlightVariables = (text: string): string => {
		const regex = /{{([^}]+)}}/g;
		return text.replace(
			regex,
			(match) =>
				`<span class="bg-indigo-50 text-indigo-600 rounded px-1 mx-0.5 font-mono text-sm border border-indigo-100">${match}</span>`,
		);
	};

	const highlightedHTML = useMemo(() => highlightVariables(currentRawValue), [currentRawValue]);

	const restoreCursorPosition = (element: HTMLElement, cursorPosition: number): void => {
		const sel = window.getSelection();
		if (!sel) {
			return;
		}

		let charCount = 0;
		let foundNode: Node | null = null;
		let foundOffset = 0;

		const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null);
		let node: Node | null = null;

		while ((node = walker.nextNode())) {
			const textLength = node.textContent?.length || 0;
			if (charCount + textLength >= cursorPosition) {
				foundNode = node;
				foundOffset = cursorPosition - charCount;
				break;
			}
			charCount += textLength;
		}

		if (foundNode) {
			const range = document.createRange();
			range.setStart(foundNode, foundOffset);
			range.collapse(true);
			sel.removeAllRanges();
			sel.addRange(range);
		}
	};

	const getGlobalCursorPosition = (): number => {
		const sel = window.getSelection();
		if (!sel?.rangeCount) {
			return 0;
		}
		const range = sel.getRangeAt(0);
		const preRange = document.createRange();
		if (inputRef.current) {
			preRange.selectNodeContents(inputRef.current);
			preRange.setEnd(range.startContainer, range.startOffset);
		}
		return preRange.toString().length;
	};

	useEffect(() => {
		if (!inputRef.current) {
			return;
		}

		const sel = window.getSelection();
		let cursorPosition: number | null = null;

		if (sel?.rangeCount) {
			const range = sel.getRangeAt(0);
			if (inputRef.current.contains(range.startContainer)) {
				cursorPosition = getGlobalCursorPosition();
			}
		}

		if (!currentRawValue.endsWith('{{')) {
			inputRef.current.innerHTML = highlightedHTML;

			if (cursorPosition !== null) {
				restoreCursorPosition(inputRef.current, cursorPosition);
			}
		}
	}, [highlightedHTML]);

	useEffect(() => {
		if (inputRef.current && value !== inputRef.current.textContent) {
			inputRef.current.textContent = value;
			if (!value.endsWith('{{')) {
				inputRef.current.innerHTML = highlightVariables(value);
			}
		}
	}, [value]);

	const handleInput = (): void => {
		if (!inputRef.current) {
			return;
		}
		const text = inputRef.current.textContent || '';
		setInternalValue(text);
		onChange?.(text);

		const sel = window.getSelection();
		if (!sel?.rangeCount) {
			return;
		}

		const range = sel.getRangeAt(0);
		const cursorPosition = getGlobalCursorPosition();

		const beforeCursor = text.slice(0, cursorPosition);
		const isTypingVariable = beforeCursor.endsWith('{{');

		if (isTypingVariable) {
			const { top, left } = range.getBoundingClientRect();
			setListPosition({ top: top + 25, left: left - 10 });
			setShowVariableList(true);
		} else {
			setShowVariableList(false);
			if (inputRef.current && !text.endsWith('{{')) {
				const newHtml = highlightVariables(text);
				inputRef.current.innerHTML = newHtml;
				restoreCursorPosition(inputRef.current, cursorPosition);
			}
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			const sel = window.getSelection();
			if (!sel?.rangeCount || !inputRef.current) {
				return;
			}

			// const range = sel.getRangeAt(0);
			const cursorPosition = getGlobalCursorPosition();
			const text = inputRef.current.textContent || '';

			// Split text at cursor position and add newline
			const beforeCursor = text.slice(0, cursorPosition);
			const afterCursor = text.slice(cursorPosition);
			const newText = beforeCursor + '\n' + afterCursor;

			inputRef.current.textContent = newText;
			setInternalValue(newText);
			onChange?.(newText);

			// Update highlighting while preserving cursor position
			if (!newText.endsWith('{{')) {
				const newHtml = highlightVariables(newText);
				inputRef.current.innerHTML = newHtml;
				restoreCursorPosition(inputRef.current, cursorPosition + 1);
			}

			return;
		}

		if (e.key === 'Backspace' && inputRef.current) {
			const text = inputRef.current.textContent || '';
			const cursorPosition = getGlobalCursorPosition();

			if (text[cursorPosition - 1] === '}' && text[cursorPosition - 2] === '}') {
				e.preventDefault();

				const beforeCursor = text.substring(0, cursorPosition);
				const openBraceIndex = beforeCursor.lastIndexOf('{{');

				if (openBraceIndex !== -1) {
					const newText = text.substring(0, openBraceIndex) + text.substring(cursorPosition);
					inputRef.current.textContent = newText;
					setInternalValue(newText);
					onChange?.(newText);

					const newHtml = highlightVariables(newText);
					inputRef.current.innerHTML = newHtml;
					restoreCursorPosition(inputRef.current, openBraceIndex);
				}
			}
		}
	};

	const insertVariable = (variable: string): void => {
		if (!Object.keys(variables).includes(variable)) {
			notification.warning({
				message: 'Variable not available',
				description: 'This variable is no longer available.',
			});
			return;
		}

		const sel = window.getSelection();
		if (!sel?.rangeCount || !inputRef.current) {
			return;
		}

		const range = sel.getRangeAt(0);
		range.setStart(range.startContainer, range.startOffset - 2);
		range.deleteContents();

		const variableText = `{{${variable}}}`;
		const textNode = document.createTextNode(variableText);
		range.insertNode(textNode);
		inputRef.current.normalize();

		const newText = inputRef.current.textContent || '';
		setInternalValue(newText);
		setShowVariableList(false);
		onChange?.(newText);

		const newHtml = highlightVariables(newText);
		inputRef.current.innerHTML = newHtml;

		const newPosition = newText.indexOf(variableText) + variableText.length;
		restoreCursorPosition(inputRef.current, newPosition);
	};

	return (
		<div className="relative">
			<div className="mb-4 relative">
				<div
					className="w-full min-h-[100px] p-3 border rounded-lg resize-y overflow-auto 
                   focus:outline-none focus:ring-2 focus:ring-blue-300 prose whitespace-pre-wrap"
					contentEditable
					ref={inputRef}
					onInput={handleInput}
					onKeyDown={handleKeyDown}
					suppressContentEditableWarning
				/>
				{!currentRawValue && (
					<span className="absolute left-4 top-4 text-gray-400 pointer-events-none">
						{placeholder}
					</span>
				)}
			</div>

			{showVariableList && (
				<div
					className="fixed bg-white border border-indigo-100 shadow-xl rounded-xl py-1.5"
					style={{
						top: listPosition.top,
						left: listPosition.left,
						zIndex: 1000,
						minWidth: '160px',
					}}
				>
					{Object.keys(variables).map((varName) => (
						<button
							key={varName}
							onClick={() => insertVariable(varName)}
							className="w-full px-4 py-2 text-sm font-mono font-medium
                 text-indigo-600 bg-indigo-50 hover:bg-indigo-100
                 transition-all duration-150"
						>
							{`{{${varName}}}`}
						</button>
					))}
				</div>
			)}
		</div>
	);
};

export default TextEditor;
